import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "captionPanel", "captionResults", "captionLoading", "captionBtn",
    "hashtagPanel", "hashtagResults", "hashtagLoading", "hashtagBtn",
    "captionField", "hashtagField", "contextInput"
  ]
  static values = {
    generateCaptionsUrl: String,
    suggestHashtagsUrl: String,
    persisted: Boolean
  }

  toggleCaptionPanel() {
    if (!this.persistedValue) {
      alert("Save this content item as a draft first, then use AI features.")
      return
    }
    if (this.hasCaptionPanelTarget) {
      this.captionPanelTarget.classList.toggle("hidden")
    }
  }

  toggleHashtagPanel() {
    if (!this.persistedValue) {
      alert("Save this content item as a draft first, then use AI features.")
      return
    }
    if (this.hasHashtagPanelTarget) {
      this.hashtagPanelTarget.classList.toggle("hidden")
    }
  }

  async generateCaptions(event) {
    event.preventDefault()

    const context = this.hasContextInputTarget ? this.contextInputTarget.value : ""
    if (!context.trim()) {
      this.contextInputTarget.focus()
      this.contextInputTarget.classList.add("border-[#E57373]")
      setTimeout(() => this.contextInputTarget.classList.remove("border-[#E57373]"), 2000)
      return
    }

    if (this.hasCaptionLoadingTarget) this.captionLoadingTarget.classList.remove("hidden")
    if (this.hasCaptionResultsTarget) this.captionResultsTarget.classList.add("hidden")
    if (this.hasCaptionBtnTarget) {
      this.captionBtnTarget.disabled = true
      this.captionBtnTarget.textContent = "Generating..."
    }

    try {
      const response = await fetch(this.generateCaptionsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        },
        body: JSON.stringify({ context })
      })

      const data = await response.json()

      if (response.ok && data.captions) {
        this.renderCaptions(data.captions)
      } else {
        this.showCaptionError(data.error || "Failed to generate captions.")
      }
    } catch (error) {
      this.showCaptionError("Network error. Please try again.")
    } finally {
      if (this.hasCaptionLoadingTarget) this.captionLoadingTarget.classList.add("hidden")
      if (this.hasCaptionBtnTarget) {
        this.captionBtnTarget.disabled = false
        this.captionBtnTarget.textContent = "Generate 3 Captions"
      }
    }
  }

  renderCaptions(captions) {
    if (!this.hasCaptionResultsTarget) return

    const html = captions.map((c, i) => `
      <div class="p-3 border border-[#E8E4DF] rounded-lg">
        <div class="flex items-center justify-between mb-1">
          <span class="text-[10px] font-medium text-[#C4A484] uppercase">Option ${String.fromCharCode(65 + i)} (${this.escapeHtml(c.style)})</span>
          <button data-action="click->caption-writer#useCaption"
                  data-caption="${this.escapeAttr(c.caption)}"
                  class="px-2 py-1 text-[10px] font-medium text-white bg-[#C4A484] hover:bg-[#B39474] rounded transition-colors cursor-pointer">
            Use This
          </button>
        </div>
        <p class="text-xs text-[#2D2D2D]">"${this.escapeHtml(c.caption)}"</p>
      </div>
    `).join("")

    this.captionResultsTarget.innerHTML = `<div class="space-y-2 mt-3">${html}</div>`
    this.captionResultsTarget.classList.remove("hidden")
  }

  useCaption(event) {
    const caption = event.currentTarget.dataset.caption
    this._setCaptionContent(caption)
    // Visual feedback
    const btn = event.currentTarget
    btn.textContent = "Applied"
    btn.classList.remove("bg-[#C4A484]", "hover:bg-[#B39474]")
    btn.classList.add("bg-[#9CAF88]")
    setTimeout(() => {
      btn.textContent = "Use This"
      btn.classList.add("bg-[#C4A484]", "hover:bg-[#B39474]")
      btn.classList.remove("bg-[#9CAF88]")
    }, 1500)
  }

  showCaptionError(message) {
    if (this.hasCaptionResultsTarget) {
      this.captionResultsTarget.innerHTML = `
        <div class="p-3 bg-[#E57373]/10 border border-[#E57373]/20 rounded-lg mt-3">
          <p class="text-xs text-[#E57373]">${this.escapeHtml(message)}</p>
        </div>
      `
      this.captionResultsTarget.classList.remove("hidden")
    }
  }

  async suggestHashtags(event) {
    event.preventDefault()

    const captionText = this._readCaptionContent()

    if (this.hasHashtagLoadingTarget) this.hashtagLoadingTarget.classList.remove("hidden")
    if (this.hasHashtagResultsTarget) this.hashtagResultsTarget.classList.add("hidden")
    if (this.hasHashtagBtnTarget) {
      this.hashtagBtnTarget.disabled = true
      this.hashtagBtnTarget.textContent = "Suggesting..."
    }

    try {
      const response = await fetch(this.suggestHashtagsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        },
        body: JSON.stringify({ caption: captionText })
      })

      const data = await response.json()

      if (response.ok && data.hashtags) {
        this.renderHashtags(data.hashtags)
      } else {
        this.showHashtagError(data.error || "Failed to suggest hashtags.")
      }
    } catch (error) {
      this.showHashtagError("Network error. Please try again.")
    } finally {
      if (this.hasHashtagLoadingTarget) this.hashtagLoadingTarget.classList.add("hidden")
      if (this.hasHashtagBtnTarget) {
        this.hashtagBtnTarget.disabled = false
        this.hashtagBtnTarget.textContent = "Suggest Hashtags"
      }
    }
  }

  renderHashtags(hashtags) {
    if (!this.hasHashtagResultsTarget) return

    const currentHashtags = this.hasHashtagFieldTarget ? this.hashtagFieldTarget.value : ""

    const pills = hashtags.map(tag => {
      const isActive = currentHashtags.includes(`#${tag}`)
      return `
        <button data-action="click->caption-writer#toggleHashtag"
                data-tag="${this.escapeAttr(tag)}"
                class="px-2.5 py-1 text-xs rounded-full border transition-colors cursor-pointer
                  ${isActive
                    ? 'bg-[#C4A484] text-white border-[#C4A484]'
                    : 'bg-white text-[#2D2D2D] border-[#E8E4DF] hover:border-[#C4A484]'
                  }">
          #${this.escapeHtml(tag)}
        </button>
      `
    }).join("")

    this.hashtagResultsTarget.innerHTML = `
      <div class="mt-3">
        <p class="text-[10px] text-[#8C8C8C] mb-2">Click to add or remove hashtags:</p>
        <div class="flex flex-wrap gap-1.5">${pills}</div>
      </div>
    `
    this.hashtagResultsTarget.classList.remove("hidden")
  }

  toggleHashtag(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const tag = btn.dataset.tag
    if (!this.hasHashtagFieldTarget) return

    let current = this.hashtagFieldTarget.value.trim()
    const hashTag = `#${tag}`

    if (current.includes(hashTag)) {
      // Remove hashtag
      current = current.replace(hashTag, "").replace(/\s+/g, " ").trim()
      btn.classList.remove("bg-[#C4A484]", "text-white", "border-[#C4A484]")
      btn.classList.add("bg-white", "text-[#2D2D2D]", "border-[#E8E4DF]")
    } else {
      // Add hashtag
      current = current ? `${current} ${hashTag}` : hashTag
      btn.classList.add("bg-[#C4A484]", "text-white", "border-[#C4A484]")
      btn.classList.remove("bg-white", "text-[#2D2D2D]", "border-[#E8E4DF]")
    }

    this.hashtagFieldTarget.value = current
    this.hashtagFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  showHashtagError(message) {
    if (this.hasHashtagResultsTarget) {
      this.hashtagResultsTarget.innerHTML = `
        <div class="p-3 bg-[#E57373]/10 border border-[#E57373]/20 rounded-lg mt-3">
          <p class="text-xs text-[#E57373]">${this.escapeHtml(message)}</p>
        </div>
      `
      this.hashtagResultsTarget.classList.remove("hidden")
    }
  }

  // Route caption insertion through the TipTap controller if the caption
  // field is wrapped by one; otherwise fall back to the plain-textarea path.
  // Keeps Generate -> Use This working whether the caption field is rich or
  // legacy plain text.
  _tiptapController() {
    if (!this.hasCaptionFieldTarget) return null
    const el = this.captionFieldTarget.closest("[data-controller~='tiptap-editor']")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "tiptap-editor")
  }

  _setCaptionContent(text) {
    const tt = this._tiptapController()
    if (tt) {
      tt.setContent(`<p>${this.escapeHtml(text)}</p>`)
      return
    }
    if (this.hasCaptionFieldTarget) {
      this.captionFieldTarget.value = text
      this.captionFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  _readCaptionContent() {
    const tt = this._tiptapController()
    if (tt) return tt.getText()
    return this.hasCaptionFieldTarget ? this.captionFieldTarget.value : ""
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }

  escapeAttr(text) {
    return (text || "").replace(/'/g, "&#39;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
